import React, { useEffect, useState, useCallback } from 'react';
import DataGrid, {
    Column,
    Selection,
    Pager,
    Paging,
    Scrolling
} from 'devextreme-react/cjs/data-grid';
import { Card, Button, Modal } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { getReturnSalesOrderItemsListData, calculateReturnSalesOrderItemPrices } from '../services/return.sales.service';
import { decryptItem } from 'helpers/secureNavigate';
import ReturnCalculationModal from './ReturnCalculationModal';
import Loader from 'Common/Loader';
import notistack from 'pages/Core/Notistack';
import { HttpStatus } from 'pages/Core';

const ReturnSalesOrderDetails: React.FC = () => {

    const { id } = useParams();
    const returnSalesOrderId = id ? Number(decryptItem(id)) : 0;
    const [data, setData] = useState<any[]>([]);
    const [selectedRows, setSelectedRows] = useState<any[]>([]);
    const [showModal, setShowModal] = useState(false);
    const [returnData, setReturnData] = useState<any>(null);
    const [returnId, setReturnId] = useState<number>(0);
    const [returnIds, setReturnIds] = useState<{ salesOrderItemId: number }[]>([]);
    const [items, setItems] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    const fetchData = async () => {
        try {
            const res = await getReturnSalesOrderItemsListData(returnSalesOrderId);
            setData(res?.data || []);
        } catch (error) {
            console.error("Error fetching items:", error);
        }
    };

    useEffect(() => {
        if (returnSalesOrderId) {
            fetchData();
        }
    }, [returnSalesOrderId]);

    const onSelectionChanged = useCallback((e: any) => {
        const validRows = e.selectedRowsData.filter(
            (item: any) => !isRowDisabled(item)
        );
        setSelectedRows(validRows);
    }, []);

    const createPayload = () => {
        return {
            returnSalesOrderId: Number(returnSalesOrderId),
            returnSalesOrderItems: selectedRows.map((item) => ({
                salesOrderItemId: item.id,
                quantity: 1
            }))
        };
    };

    const handleCalculate = async () => {
        try {
            if (!selectedRows.length) {
                notistack.warning("Please select at least one item");
                return;
            }

            setLoading(true);

            const payload = createPayload();
            const res = await calculateReturnSalesOrderItemPrices(payload);

            if (res.status === HttpStatus.Success) {
                setReturnId(returnSalesOrderId)
                setReturnIds(
                    selectedRows.map((item) => ({
                        salesOrderItemId: item.id,
                    }))
                );
                setItems(res?.data?.returnSalesOrderItems || []);
                setReturnData(res?.data);
                setShowModal(true);
            } else {
                notistack.notifyFromApi(res)
            }
        } catch (error) {
            console.error("Calculation error:", error);
        } finally {
            setLoading(false);
        }
    };

    const isRowDisabled = (data: any) => {
        return !(typeof data?.status === "string" && data?.status?.trim() === "Order Received for QC");
    };

    const handleCellPrepared = useCallback((e: any) => {
        if (
            e.rowType === "data" &&
            e.column.command === "select" &&
            isRowDisabled(e.data)
        ) {
            e.cellElement.innerHTML = "";
        }
    }, []);

    const handleRowPrepared = useCallback((e: any) => {
        if (e.rowType === "data" && isRowDisabled(e.data)) {
            e.rowElement.classList.add("dx-row-disabled");
        }
    }, []);

    return (
        <div className="page-content">
            {loading && <Loader />}
            <div className="container-fluid">
                <Card>
                    <Card.Header className='d-flex justify-content-between align-items-center'>
                        <h5 className='m-0'>Process QC</h5>

                        <div className='d-flex gap-2'>
                            <Button
                                className='btn submit-btn'
                                onClick={handleCalculate}
                                disabled={loading || selectedRows.length === 0}
                            >
                                {loading ? "Calculating..." : "Calculate Price"}
                            </Button>
                        </div>
                    </Card.Header>

                    <Card.Body>

                        <DataGrid
                            dataSource={data}
                            keyExpr="id"
                            showBorders={true}
                            columnAutoWidth={true}
                            onSelectionChanged={onSelectionChanged}
                            onRowPrepared={handleRowPrepared}
                            onCellPrepared={handleCellPrepared}
                        >

                            <Selection
                                mode="multiple"
                                showCheckBoxesMode="always"
                            />

                            <Paging defaultPageSize={10} />

                            <Pager
                                showPageSizeSelector={true}
                                allowedPageSizes={[10, 25, 50]}
                                showInfo={true}
                            />

                            <Scrolling
                                mode="standard"
                                useNative={true}
                                scrollByContent={true}
                                scrollByThumb={true}
                            />
                            <Column
                                dataField="id"
                                dataType="number"
                                sortOrder="desc"
                                visible={false}
                            />

                            <Column dataField="bagNo" caption="Bag No." width={250} />
                            <Column dataField="styleNo" caption="Style No." />

                            <Column dataField="grossWT" caption="Gross Wt" format="#,##0.00" />
                            <Column dataField="netWT" caption="Net Wt" format="#,##0.00" />
                            <Column dataField="productPrice" caption="Product Price" format="#,##0.00" />
                            <Column dataField="deductPrice" caption="Deduct Price" format="#,##0.00" />
                            <Column dataField="status" caption="Status" />
                            <Column dataField="waybill" caption="Way bill" />
                            <Column dataField="label" caption="Shipping Label"
                                cellRender={(cellData) => {
                                    const url = cellData?.data?.label && cellData?.data?.label;
                                    if (url)
                                        return (
                                            <a
                                                href={url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                download
                                                style={{ color: "#0d6efd", textDecoration: "underline", cursor: "pointer" }}
                                            >
                                                Shipping Label
                                            </a>
                                        );
                                }}
                            />
                        </DataGrid>

                    </Card.Body>
                </Card>
            </div>

            <ReturnCalculationModal
                show={showModal}
                onClose={() => setShowModal(false)}
                returnData={returnData}
                items={items}
                returnId={returnId}
                returnIds={returnIds}
            />
        </div>
    );
};

export default ReturnSalesOrderDetails;